import type { IEmailModalFormValues } from './types'
import type { IUserProfile } from 'containers/user-profile/types'

import { Box, Grid } from '@mui/material'
import Button from '@rentspree/component-2023.components.atoms.button'
import TextInput from '@rentspree/component-2023.components.atoms.text-input'
import StickyFooterTemplate from '@rentspree/component-2023.components.templates.sticky-footer-template'
import { Form, Formik } from 'formik'
import { useState } from 'react'
import Turnstile from 'react-turnstile'

import env from 'env'

import { contactAgentEmailModalFormValidation } from './validations'

interface IContactAgentEmailFormProps {
  handleSubmit: (values: IEmailModalFormValues) => void
  isLoading?: boolean
  defaultMessage: string
  profile?: IUserProfile
  prevFormData?: IEmailModalFormValues
}

export const ContactAgentEmailForm: React.FC<IContactAgentEmailFormProps> = props => {
  const { handleSubmit, isLoading = false, defaultMessage, prevFormData = undefined } = props
  const initialValues: IEmailModalFormValues = prevFormData || {
    message: defaultMessage,
  }
  const isTurnstileEnabled = env?.TURNSTILE_ENABLED
  const [isTurnstileLoading, setIsTurnstileLoading] = useState(false)

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={contactAgentEmailModalFormValidation}
      onSubmit={handleSubmit}
    >
      {({ handleChange, handleBlur, setFieldValue, setFieldTouched, values, errors, touched }) => (
        <Form>
          <Box className="contact-agent-email-form-container">
            <Grid className="contact-agent-email-form-body" container spacing={2}>
              <Grid item xs={12}>
                <TextInput
                  id="emailMessage"
                  required
                  inputProps={{
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'data-testid': 'contact-agent-email-form-message',
                  }}
                  name="message"
                  label="Message"
                  placeholder={defaultMessage}
                  error={!!touched?.message && !!errors?.message}
                  helperText={errors.message ?? ''}
                  value={values.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onFocus={(event: React.FocusEvent<HTMLTextAreaElement>) => {
                    if (event.target.value === '') {
                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                      setFieldTouched('message', true, true)
                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                      setFieldValue('message', defaultMessage)
                    }
                  }}
                  InputLabelProps={{
                    shrink: Boolean(values.message) || touched.message,
                  }}
                  disabled={isLoading}
                  multiline
                  rows={6}
                />
              </Grid>
              {isTurnstileEnabled && (
                <Grid item xs={12}>
                  <Turnstile
                    theme="light"
                    sitekey={env?.TURNSTILE_SITE_KEY}
                    action="rentspree-public-message"
                    size="flexible"
                    refreshExpired="auto"
                    onLoad={() => setIsTurnstileLoading(true)}
                    onSuccess={() => setIsTurnstileLoading(false)}
                    onVerify={() => setIsTurnstileLoading(false)}
                  />
                </Grid>
              )}
            </Grid>
            <Box className="contact-agent-email-form-footer-container">
              <StickyFooterTemplate>
                <Box className="contact-agent-email-form-footer-cta-wrapper">
                  <Button
                    id="sendEmailButton"
                    className="contact-agent-email-form-footer-cta-submit-button"
                    data-testid="contact-agent-email-form-submit-button"
                    type="submit"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    disabled={isTurnstileLoading || isLoading}
                  >
                    Send message
                  </Button>
                </Box>
              </StickyFooterTemplate>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  )
}
